import type { ProjectDetail } from "@esi/shared";
import type { ComponentType } from "react";
import { can } from "../capabilities.ts";
import { PeopleSection, SourcesFiguresSection, WikiFiguresSection } from "./Figures.tsx";
import { NeedsYou } from "./NeedsYou.tsx";
import { RecentConversations } from "./RecentConversations.tsx";
import { UsageSection } from "./Usage.tsx";
import { WhatChanged } from "./WhatChanged.tsx";

/**
 * One section of Overview (design D4). `visible` reads only the viewer's capabilities, never
 * their role's name, so the permission matrix stays the one source; a section it hides is
 * omitted, never shown empty. A later section (Usage, pinned pages, milestones) is one entry.
 */
export interface HomeSectionEntry {
  id: string;
  /** `main` holds the lists, `side` the figures (two columns from 1024px). */
  column: "main" | "side";
  order: number;
  visible: (project: Pick<ProjectDetail, "capabilities">) => boolean;
  Component: ComponentType<{ project: ProjectDetail }>;
}

export const HOME_SECTIONS: readonly HomeSectionEntry[] = [
  { id: "needs-you", column: "main", order: 10, visible: () => true, Component: NeedsYou },
  {
    id: "recent-conversations",
    column: "main",
    order: 15,
    visible: () => true,
    Component: RecentConversations,
  },
  { id: "what-changed", column: "main", order: 20, visible: () => true, Component: WhatChanged },
  { id: "wiki", column: "side", order: 10, visible: () => true, Component: WikiFiguresSection },
  {
    id: "sources",
    column: "side",
    order: 20,
    visible: () => true,
    Component: SourcesFiguresSection,
  },
  {
    id: "people",
    column: "side",
    order: 30,
    visible: (project) => can(project, "project.view_members"),
    Component: PeopleSection,
  },
  {
    id: "usage",
    column: "side",
    order: 40,
    visible: (project) => can(project, "project.view_activity"),
    Component: UsageSection,
  },
];

/**
 * The sections the viewer may see, in each column's order. One column (below 1024px) lists the
 * main column, then the side one: Needs you, Recent conversations, What changed, Wiki, Sources,
 * People, Usage.
 */
export function visibleSections(
  project: Pick<ProjectDetail, "capabilities">,
  sections: readonly HomeSectionEntry[] = HOME_SECTIONS,
): { main: HomeSectionEntry[]; side: HomeSectionEntry[] } {
  const shown = sections.filter((s) => s.visible(project)).sort((a, b) => a.order - b.order);
  return {
    main: shown.filter((s) => s.column === "main"),
    side: shown.filter((s) => s.column === "side"),
  };
}
